import { useRef, useState } from 'react';
import { isEmptyString } from '@/utils/string';
import { WAREHOUSE_DESTINATION_REQUIRED_FIELDS } from '@/features/warehouse-sync';
import type { BucketConfigDraft, BucketConfigField } from '@/features/warehouse-sync';
import type { WarehouseDestination } from '@/services/db/warehouse-sync/entity';
import { PROVIDER_VALIDATORS } from './validators';
import type { BucketConfigMode, ProviderFormProps } from './types';

type FieldErrors = Partial<Record<BucketConfigField, string>>;

type UseBucketConfigDraftArgs = {
  destination: WarehouseDestination;
  mode: BucketConfigMode;
  /**
   * Seeds the draft, read once. Both callers re-mount rather than re-seed — the
   * wizard is keyed on its destination, the modal is mounted only while open — so
   * a later change to this value is not picked up by design.
   */
  initialConfig: BucketConfigDraft;
  isDisabled: boolean;
};

/**
 * The editing state behind a provider form: the draft config, its field errors, and
 * the props the form itself takes.
 *
 * Shared by the create wizard and the "Manage configuration" modal so the two agree
 * on when a field is invalid — they differ only in `mode`, which decides whether a
 * blank secret reads as missing or as needing to be typed again.
 */
export function useBucketConfigDraft({ destination, mode, initialConfig, isDisabled }: UseBucketConfigDraftArgs) {
  const [config, setConfig] = useState<BucketConfigDraft>(() => ({ ...initialConfig }));
  const [errors, setErrors] = useState<FieldErrors>({});

  // The ref is the authoritative draft; the state exists to re-render from. A
  // select validates the value it just produced, in the same tick as the change,
  // so reading the config through the render closure would validate the value
  // before it — the stale read that leaves a chosen region reading as blank.
  const configRef = useRef<BucketConfigDraft>(config);

  const validate = (): FieldErrors => PROVIDER_VALIDATORS[destination](configRef.current, mode);

  const onChange = (field: BucketConfigField, value: string) => {
    configRef.current = { ...configRef.current, [field]: value };
    setConfig(configRef.current);
    // Typing clears the field's error immediately — waiting for blur leaves the
    // submit button disabled while the user looks at a field they have already fixed.
    setErrors((current) => (current[field] === undefined ? current : { ...current, [field]: '' }));
  };

  const onBlur = (field: BucketConfigField) => {
    const fieldErrors = validate();
    setErrors((current) => ({ ...current, [field]: fieldErrors[field] ?? '' }));
  };

  /** Validates every field and shows the results. True when the config is submittable. */
  const validateAll = (): boolean => {
    const fieldErrors = validate();
    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  };

  // Cleared fields are kept as empty strings so the input can re-render without one;
  // only non-empty messages count as errors.
  const hasErrors = Object.values(errors).some((message) => message !== '');

  // "Complete" means the required credentials are actually present — an untouched
  // form has no errors yet, and must not read as done. `bucket_path` is optional
  // for every provider, so it is absent from the required list.
  const hasRequiredFields = WAREHOUSE_DESTINATION_REQUIRED_FIELDS[destination].every(
    (field) => !isEmptyString(config[field]),
  );

  const form: ProviderFormProps = { config, errors, isDisabled, onChange, onBlur };

  return {
    errors,
    form,
    hasErrors,
    hasRequiredFields,
    validateAll,
    /**
     * The draft to submit. Reads the ref rather than the render's state, for the
     * same reason `validateAll` does — a select can change the config and submit
     * in one handler, and the state copy would still hold the previous value.
     */
    getConfig: () => configRef.current,
  };
}
